/**
 * Attachment API Functions
 * 
 * Type-safe functions for calling backend attachment methods.
 * All functions use frappeMethod for consistency with existing codebase.
 */

import { frappeMethod } from "@/lib/erp/utils";
import type {
  AttachmentContext,
  GetAttachmentsResponse,
  GetFileLibraryResponse,
  GetFileLibraryParams,
  AttachUploadedFileParams,
  CreateLinkAttachmentParams,
  DeleteAttachmentParams,
  UploadAndAttachParams,
} from "./types";

/**
 * Get attachments for a document
 * 
 * @param context - Attachment context (doctype, name, optional fieldname)
 * @returns List of attachments
 */
export async function getAttachments(
  context: AttachmentContext
): Promise<GetAttachmentsResponse> {
  return frappeMethod<GetAttachmentsResponse>(
    "zaviago_backend.api.attachments.get_attachments",
    {
      doctype: context.doctype,
      name: context.name,
      fieldname: context.fieldname || null,
    }
  );
}

/**
 * Browse file library with search and filters
 * 
 * @param params - Search and pagination parameters
 * @returns Paginated list of files
 */
export async function getFileLibrary(
  params: GetFileLibraryParams = {}
): Promise<GetFileLibraryResponse> {
  return frappeMethod<GetFileLibraryResponse>(
    "zaviago_backend.api.attachments.get_file_library",
    {
      query: params.query || null,
      mime_type: params.mime_type || null,
      page: params.page || 1,
      page_size: params.page_size || 20,
    }
  );
}

/**
 * Attach an already uploaded file to a document
 * 
 * @param params - Attachment parameters
 * @returns Updated attachment list
 */
export async function attachUploadedFile(
  params: AttachUploadedFileParams
): Promise<GetAttachmentsResponse> {
  return frappeMethod<GetAttachmentsResponse>(
    "zaviago_backend.api.attachments.attach_uploaded_file",
    {
      doctype: params.doctype,
      name: params.name,
      fieldname: params.fieldname || null,
      file_url: params.file_url,
      file_name: params.file_name || null,
    }
  );
}

/**
 * Create a URL-based link attachment
 * 
 * @param params - Link attachment parameters
 * @returns Updated attachment list
 */
export async function createLinkAttachment(
  params: CreateLinkAttachmentParams
): Promise<GetAttachmentsResponse> {
  return frappeMethod<GetAttachmentsResponse>(
    "zaviago_backend.api.attachments.create_link_attachment",
    {
      doctype: params.doctype,
      name: params.name,
      fieldname: params.fieldname || null,
      url: params.url,
      label: params.label || null,
    }
  );
}

/**
 * Delete an attachment from a document
 * 
 * @param params - Delete parameters (either file_docname or file_url required)
 * @returns Updated attachment list
 */
export async function deleteAttachment(
  params: DeleteAttachmentParams
): Promise<GetAttachmentsResponse> {
  return frappeMethod<GetAttachmentsResponse>(
    "zaviago_backend.api.attachments.delete_attachment",
    {
      doctype: params.doctype,
      name: params.name,
      file_docname: params.file_docname || null,
      file_url: params.file_url || null,
    }
  );
}

/**
 * Upload a file and attach it in one call
 * 
 * Note: This is optional. Frontend can also use standard /api/method/upload_file
 * and then call attachUploadedFile.
 * 
 * @param params - Upload and attach parameters
 * @returns Updated attachment list
 */
export async function uploadAndAttach(
  params: UploadAndAttachParams
): Promise<GetAttachmentsResponse> {
  return frappeMethod<GetAttachmentsResponse>(
    "zaviago_backend.api.attachments.upload_and_attach",
    {
      doctype: params.doctype,
      name: params.name,
      fieldname: params.fieldname || null,
      file_name: params.file_name,
      content: params.content || null,
      is_private: params.is_private || 0,
    }
  );
}
